<template>
  <div>
    <dialog-confirm ref="reprocess-confirm" :confirm-text="$t('reprocess_file_upload')">
      <template #title>{{ $t('reprocess_file_upload') }}</template>
      {{ $t('reprocess_file_upload_confirmation', { filename: reprocessTarget ? reprocessTarget.original_filename : '' }) }}
    </dialog-confirm>
    <v-sheet outlined rounded class="px-4 py-2 mb-4">
      <div class="text-subtitle-1">{{ $t('filter_by') }}</div>
      <v-row dense>
        <v-col md="4" cols="12">
          <v-text-field
            :label="$t('filename')"
            v-model="searchCriteria.filename"
            @keyup.enter="initiateSearch"
            prepend-inner-icon="mdi-file">
          </v-text-field>
        </v-col>
        <v-col md="4" cols="12">
          <v-select
            :label="$t('status')"
            v-model="searchCriteria.status"
            :items="statusOptions"
            @change="initiateSearch"
            prepend-inner-icon="mdi-filter"/>
        </v-col>
        <v-col md="4" cols="12">
          <v-select
            :label="$t('upload_method')"
            v-model="searchCriteria.upload_method"
            :items="uploadMethodOptions"
            @change="initiateSearch"
            prepend-inner-icon="mdi-cloud-upload"/>
        </v-col>
        <v-col class="d-flex align-center" md="6" cols="12">
          <date-picker
            v-model="searchCriteria.uploaded_after"
            :timezone="customerTimezone"
            :label="$t('uploaded_from')"
            prepend-inner-icon="mdi-calendar"
            reset-on-timezone-change />
          <date-picker
            v-model="searchCriteria.uploaded_before"
            :timezone="customerTimezone"
            :label="$t('uploaded_to')"
            prepend-inner-icon="mdi-calendar"
            end-of-day
            reset-on-timezone-change />
        </v-col>
      </v-row>
      <div class="d-flex justify-end mt-1 mb-2">
        <v-btn outlined color="primary" @click="initiateSearch()" :loading="remoteData.isBusy">
          <v-icon class="mr-1 ml-n1">mdi-magnify</v-icon>
          {{ $t('search') }}
        </v-btn>
        <v-btn outlined color="primary" class="ml-2" @click="resetTable()" :disabled="tableControlsMatchDefaults">
          <v-icon class="mr-1 ml-n1">mdi-filter-remove</v-icon>
          {{ $t('reset') }}
        </v-btn>
      </div>
    </v-sheet>

    <div class="d-flex justify-space-between align-center mb-2">
      <div class="text-subtitle-1 font-weight-bold">{{ $t('file_upload_records') }}</div>
      <table-pagination-controls
        :total-rows="remoteData.totalRows"
        :current-page.sync="remoteData.currentPage"
        :rows-per-page.sync="remoteData.rowsPerPage"
        @update:currentPage="refreshResults"
        @update:rowsPerPage="initiateSearch"
        :per-page-options="remoteData.pageOptions"/>
      
      <div>
        <v-btn
          outlined
          color="primary"
          class="hidden-md-and-down"
          @click="downloadRemoteDataCsv()"
          :loading="remoteData.isDownloading">
          <v-icon class="mr-1 ml-n1">mdi-tray-arrow-down</v-icon>
          {{ $t('download') }}
        </v-btn>
      </div>
    </div>

    <div class="data-table-wrapper" :class="{'loading': remoteData.isBusy}">
      <v-data-table
        hide-default-footer
        class="vf-table"
        :headers="tableFields"
        :items="remoteData.items"
        :server-items-length="remoteData.totalRows"
        :sort-by.sync="remoteData.sortBy"
        :sort-desc.sync="remoteData.sortDesc"
        :loading="remoteData.isBusy"
        :loading-text="$t('loading')"
        @update:sort-by="initiateSearch()"
        @update:sort-desc="initiateSearch()"
        @click:row="handleRowClick">
        

        <template #item.status="row">
          <v-chip 
            :color="getStatusColor(row.value)" 
            :text-color="getStatusTextColor(row.value)"
            small
            label>
            <v-icon left small>{{ getStatusIcon(row.value) }}</v-icon>
            {{ getStatusText(row.value) }}
          </v-chip>
        </template>

        <template #item.upload_method="row">
          <v-chip small outlined>
            <v-icon left small>{{ getUploadMethodIcon(row.value) }}</v-icon>
            {{ getUploadMethodText(row.value) }}
          </v-chip>
        </template>

        <template #item.progress="row">
          <div class="d-flex align-center">
            <v-progress-linear
              :value="getProgressPercentage(row.item)"
              :color="getProgressColor(row.item)"
              height="8"
              rounded
              class="mr-2"
              style="min-width: 80px;">
            </v-progress-linear>
            <span class="text-caption">{{ getProgressText(row.item) }}</span>
          </div>
        </template>

        <template #item.summary="row">
          <div class="text-caption">
            <div v-if="row.item.total_routes_in_file > 0">
              <v-icon small color="success" class="mr-1">mdi-check-circle</v-icon>
              {{ row.item.route_plans_created + row.item.route_plans_updated }} processed
              <span v-if="row.item.route_plans_failed > 0">
                <v-icon small color="error" class="ml-2 mr-1">mdi-alert-circle</v-icon>
                {{ row.item.route_plans_failed }} failed
              </span>
              <span v-if="row.item.route_plans_excluded > 0">
                <v-icon small color="grey" class="ml-2 mr-1">mdi-cancel</v-icon>
                {{ row.item.route_plans_excluded }} excluded
              </span>
            </div>
            <div v-else class="text--secondary">
              {{ getFileUploadStatusMessage(row.item) }}
            </div>
          </div>
        </template>

        <template #item.uploaded_at="row">
          <multi-time-display :time="row.value" />
        </template>


        <template #item.actions="row">
          <div class="d-flex">
            <vf-tooltip text="View Details">
              <v-btn
                icon
                small
                color="primary"
                @click.stop="showFileUploadDetails(row.item)">
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </vf-tooltip>
            
            <vf-tooltip 
              v-if="canReprocess(row.item)"
              text="Reprocess File Upload">
              <v-btn
                icon
                small
                color="warning"
                @click.stop="reprocessFileUpload(row.item)">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </vf-tooltip>
            
          </div>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay'
import TablePaginationControls from '@/components/shared/TablePaginationControls'
import DatePicker from '@/components/shared/DatePicker'
import VfTooltip from '@/components/shared/VfTooltip'
import DialogConfirm from '@/components/shared/DialogConfirm'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import DateTimeUtilsMixin from '@/components/mixins/DateTimeUtilsMixin'
import RemoteDataTableMixin from '@/components/mixins/RemoteDataTableMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import { some } from 'lodash'

export default {
  name: 'RoutePlanFileUploadList',
  components: {
    MultiTimeDisplay,
    TablePaginationControls,
    DatePicker,
    VfTooltip,
    DialogConfirm
  },
  mixins: [
    CurrentUserMixin,
    RemoteDataTableMixin,
    DateTimeUtilsMixin,
    BrowserUtilitiesMixin,
    ApiUtilitiesMixin
  ],
  data () {
    return {
      model: 'RoutePlanFileUpload',
      reprocessTarget: null,
      searchCriteria: this.getDefaultSearchCriteria(),
      tableFields: [
        {
          value: 'uploaded_at',
          text: this.$t('uploaded_at'),
          sortable: true
        },
        {
          value: 'status',
          text: this.$t('status'),
          sortable: true
        },
        {
          value: 'upload_method',
          text: this.$t('upload_method'),
          sortable: true
        },
        {
          value: 'progress',
          text: this.$t('progress'),
          sortable: false
        },
        {
          value: 'summary',
          text: this.$t('summary'),
          sortable: false
        },
        {
          value: 'actions',
          text: this.$t('actions'),
          sortable: false,
          width: '120'
        }
      ],
      statusOptions: [
        { value: 'all', text: this.$t('all_statuses') },
        { value: 'pending', text: this.$t('pending') },
        { value: 'processing', text: this.$t('processing') },
        { value: 'completed', text: this.$t('completed') },
        { value: 'failed', text: this.$t('failed') },
        { value: 'partially_failed', text: this.$t('partially_failed') }
      ],
      uploadMethodOptions: [
        { value: 'all', text: this.$t('all_methods') },
        { value: 'sftp', text: 'SFTP' },
        { value: 'gui', text: this.$t('web_interface') },
        { value: 'api', text: 'API' }
      ]
    }
  },
  methods: {
    getDefaultSearchCriteria () {
      return {
        filename: null,
        status: 'all',
        upload_method: 'all',
        uploaded_after: null,
        uploaded_before: null
      }
    },

    resetTable () {
      this.searchCriteria = this.getDefaultSearchCriteria()
      this.resetRemoteDataTableNavigation()
      this.refreshResults()
    },

    buildSearchCriteria () {
      const searchCriteria = {
        customer_id: this.customerContext.id,
        page: this.remoteData.currentPage,
        'per-page': this.remoteData.rowsPerPage
      }

      if (this.searchCriteria.filename) {
        searchCriteria.filename = this.searchCriteria.filename
      }
      if (this.searchCriteria.status !== 'all') {
        searchCriteria.status = this.searchCriteria.status
      }
      if (this.searchCriteria.upload_method !== 'all') {
        searchCriteria.upload_method = this.searchCriteria.upload_method
      }
      if (this.searchCriteria.uploaded_after) {
        searchCriteria.uploaded_after = this.searchCriteria.uploaded_after
      }
      if (this.searchCriteria.uploaded_before) {
        searchCriteria.uploaded_before = this.searchCriteria.uploaded_before
      }

      if (this.remoteData.sortBy) {
        const sortDirection = this.remoteData.sortDesc ? '-' : ''
        searchCriteria.sort = `${sortDirection}${this.remoteData.sortBy}`
      }

      return searchCriteria
    },


    handleRowClick (fileUploadRecord) {
      this.$emit('row-click', fileUploadRecord)
    },

    showFileUploadDetails (fileUploadRecord) {
      this.$emit('show-details', fileUploadRecord)
    },

    async reprocessFileUpload (fileUploadRecord) {
      this.reprocessTarget = fileUploadRecord
      const confirmed = await this.$refs['reprocess-confirm'].confirm()
      if (confirmed) {
        this.handleReprocess(fileUploadRecord.id)
      }
    },

    async handleReprocess (importId) {
      try {
        const response = await this.$httpAdapter.post(`route-plan-file-upload/${importId}/reprocess`)

        if (response.data.success) {
          this.$store.dispatch('globalMessagingModule/showSuccessFlashBanner', this.$t('file_upload_reprocessing_initiated'))
          // Let the parent reload the table + stats (same path as the modal flow
          // and the Refresh button) so the row flips from "failed" to "processing".
          this.$emit('reprocess', importId)
        } else {
          this.$store.dispatch('globalMessagingModule/showErrorFlashBanner', response.data.message || this.$t('reprocessing_failed'))
        }
      } catch (error) {
        console.error('Error reprocessing import:', error)
        this.$store.dispatch('globalMessagingModule/showErrorFlashBanner', this.$t('reprocessing_failed'))
      }
    },


    canReprocess (fileUploadRecord) {
      return ['failed', 'partially_failed'].includes(fileUploadRecord.status)
    },

    // Optimistically flip a row to "processing" the moment a reprocess is queued,
    // so the UI updates instantly without waiting on a re-fetch (which can briefly
    // read a stale "failed" from the read replica). refreshData() then reconciles.
    markProcessing (importId) {
      const row = this.remoteData.items.find(item => String(item.id) === String(importId))
      if (row) {
        row.status = 'processing'
        return true
      }
      return false
    },

    getStatusColor (status) {
      switch (status) {
        case 'completed': return 'success'
        case 'processing': return 'primary'
        case 'pending': return 'grey'
        case 'failed': return 'error'
        case 'partially_failed': return 'warning'
        default: return 'grey'
      }
    },

    getStatusTextColor (status) {
      return ['pending', 'grey'].includes(status) ? 'black' : 'white'
    },

    getStatusIcon (status) {
      switch (status) {
        case 'completed': return 'mdi-check-circle'
        case 'processing': return 'mdi-loading'
        case 'pending': return 'mdi-clock-outline'
        case 'failed': return 'mdi-alert-circle'
        case 'partially_failed': return 'mdi-alert'
        default: return 'mdi-help-circle'
      }
    },

    getStatusText (status) {
      return this.statusOptions.find(opt => opt.value === status)?.text || status
    },

    getUploadMethodIcon (method) {
      switch (method) {
        case 'sftp': return 'mdi-server'
        case 'gui': return 'mdi-web'
        case 'api': return 'mdi-api'
        default: return 'mdi-upload'
      }
    },

    getUploadMethodText (method) {
      return this.uploadMethodOptions.find(opt => opt.value === method)?.text || method
    },

    getProgressPercentage (item) {
      if (item.total_routes_in_file === 0) return 0
      return Math.round((item.processed_count / item.total_routes_in_file) * 100)
    },

    getProgressColor (item) {
      if (item.status === 'completed') return 'success'
      if (item.status === 'failed') return 'error'
      if (item.status === 'partially_failed') return 'warning'
      if (item.status === 'processing') return 'primary'
      return 'grey'
    },

    getProgressText (item) {
      if (item.total_routes_in_file === 0) {
        return this.getFileUploadStatusMessage(item)
      }
      return `${item.processed_count}/${item.total_routes_in_file}`
    },

    getFileUploadStatusMessage (item) {
      switch (item.status) {
        case 'pending': return this.$t('awaiting_processing')
        case 'processing': return this.$t('processing')
        case 'failed': return this.$t('processing_failed')
        default: return ''
      }
    },


    customerContextChangeHandler () {
      this.initiateSearch()
    },

    // Override to use different analytics category  
    initiateSearch () {
      this.remoteData.currentPage = 1
      this.$gtag.event('search', { event_category: 'RoutePlan' })
      this.refreshResults()
    },

    // Override to use different analytics category
    async downloadRemoteDataCsv () {
      this.remoteData.isDownloading = true
      this.$gtag.event('download', { event_category: 'RoutePlan' })

      const searchCriteria = this.buildSearchCriteria()
      delete searchCriteria.expand
      delete searchCriteria.fields
      delete searchCriteria.page
      delete searchCriteria['per-page']

      const request = {
        url: this.$orm(this.model).endpoint,
        method: 'GET',
        headers: {
          Accept: 'text/csv'
        },
        params: searchCriteria,
        responseType: 'blob'
      }

      try {
        const response = await this.$httpAdapter(request)
        const headerLine = response.headers['content-disposition']
        const startFileNameIndex = headerLine.indexOf('"') + 1
        const endFileNameIndex = headerLine.lastIndexOf('"')
        const fileName = headerLine.substring(startFileNameIndex, endFileNameIndex)
        const FileSaver = require('file-saver')
        FileSaver.saveAs(new Blob([response.data]), fileName)
      } catch (error) {
        if (error) {
          await this.$store.dispatch('globalMessagingModule/showModal', {
            message: this.$t('network_error_message')
          })
          throw (error)
        }
      } finally {
        this.remoteData.isDownloading = false
      }
    },

    // Expose this method so parent components can refresh the list
    refreshResults () {
      this.remoteData.isBusy = true
      this.loadRemoteDataDelayed()
    },

  },

  mounted () {
    this.updateBrowserTitle(this.$t('route_plan_imports'))
    this.initiateSearch()
  }
}
</script>